'use client'
import Image from 'next/image'
import { getPath } from '@/utils/getHubPath'
import type { RichTextField } from '@/types/prismic'
import { PrismicNextImage } from '@prismicio/next'
import { PrismicRichText, PrismicLink } from '@prismicio/react'

import { useLocale } from '@/context/LocaleContext'

type DataObject = Record<string, any>
type ItemObject = Record<string, any>
type HubProcessType = {
  title: RichTextField
  items: DataObject
  pathType: string
}

const HubProcess = ({ title, items, pathType }: HubProcessType) => {
  const locale = useLocale()
  return (
    <section className="py-14">
      <div className="container">
        <div className="[&>h2]:mb-14 [&>h2]:text-center [&>h2]:text-[40px] [&>h2]:leading-[44px] [&>h2]:tracking-normal">
          <PrismicRichText field={title} />
        </div>
        <div className="relative grid gap-8 lg:grid-cols-3">
          {items.map((item: ItemObject, index: number) => {
            const key = `i-${index}`
            return (
              <PrismicLink
                href={`${getPath(pathType, locale)}${item.page_url}`}
                className="group flex h-full rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary-green-85 focus-visible:ring-offset-2"
                key={key}>
                <div className="relative flex h-full w-full origin-center flex-col rounded-md border border-primary-ink-6 px-6 pb-8 pt-6 shadow-light backdrop-blur-25 transition-transform duration-300 ease-out group-hover:scale-[1.01] motion-reduce:transition-none motion-reduce:group-hover:scale-100">
                  <div className="mb-6 flex items-center justify-between gap-3">
                    <div className="relative h-12 w-12 shrink-0 rounded-full bg-secondary-gray-150">
                      <div className="[&>img]:absolute [&>img]:left-[50%] [&>img]:top-[50%] [&>img]:translate-x-[-50%] [&>img]:translate-y-[-50%]">
                        <PrismicNextImage field={item.process_icon_2} fallbackAlt="" />
                      </div>
                    </div>
                    <span className="rounded-sm bg-secondary-gray-150 p-2 text-[12px] uppercase leading-none tracking-wide">
                      {item.process_type}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="[&>h3]:mb-8 [&>h3]:text-[22px] [&>h3]:leading-6">
                      <PrismicRichText field={item.process_title} />
                    </div>
                    <div className="flex-1 [&>p]:min-h-[200px] [&>p]:text-primary-ink-60">
                      <PrismicRichText field={item.process_description} />
                    </div>
                    <div className="btn-interaction mt-auto flex !w-fit gap-1 !p-0">
                      <span>Learn more</span>
                      <Image src="/images/angle-right-green.svg" width={16} height={9} alt="" aria-hidden="true" />
                    </div>
                  </div>
                </div>
              </PrismicLink>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default HubProcess
